import { ReactNode, useEffect } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Form from "@atlaskit/form";
import { N0, N100, R400 } from "@atlaskit/theme/colors";

import { trackDataFoundationsAccountCreationComponent } from "@/components/signup/utils";
import { Link } from "@/libraries/design/components";

import { useAnalyticsClient } from "../../../../../../../../analytics/wac-analytics/analytics-client";
import { ButtonSubmit, InputEmail, SignInButtons } from "../../components";
import { useSignupFormContext } from "../../hooks";

export const EmailForm = () => {
    const {
        isEmbedded,
        productKey,
        edition,
        formError,
        hasSubmitted,
        isEmailValid,
        onSubmitWithReCAPTCHA,
        setIsEmailValid,
        setTypeAheadEmail
    } = useSignupFormContext();
    const intl = useIntl();
    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const separatorText = intl.formatMessage({
        id: "BxpUiSignup.SignupForm.or",
        defaultMessage: "Or continue with"
    });
    useEffect(() => {
        const dataFoundationsActionSubjectId = !!isEmbedded
            ? "embedded"
            : "standalone";
        if (analyticsClient) {
            trackDataFoundationsAccountCreationComponent(createAnalyticsEvent, {
                actionSubjectId: dataFoundationsActionSubjectId,
                productKey,
                edition
            });
        }
    }, [
        createAnalyticsEvent,
        productKey,
        edition,
        isEmbedded,
        analyticsClient
    ]);

    return (
        <Form onSubmit={onSubmitWithReCAPTCHA} isDisabled={false}>
            {({ formProps, submitting }) => (
                <form {...formProps} autoComplete="off">
                    <InputEmail
                        setIsEmailValid={setIsEmailValid}
                        submitting={submitting}
                        setTypeAheadEmail={setTypeAheadEmail}
                        workCallout
                    />
                    <ButtonSubmit
                        data-testid="signup-form-submit"
                        submitting={submitting}
                        hasSubmitted={hasSubmitted}
                        isEmailValid={isEmailValid}
                        formError={false}
                    />
                    {formError && (
                        <p css={formErrorStyle}>
                            <FormattedMessage
                                id="BxpUiSignup.SignupForm.formError"
                                defaultMessage="It's taking longer than expected to get you started. Please check your inbox to see if the order has completed successfully, or <contactUsLink>contact us</contactUsLink>."
                                values={{
                                    contactUsLink: (text: ReactNode) => (
                                        <Link
                                            text={text as unknown as string}
                                            aria-label={
                                                text as unknown as string
                                            }
                                            type="inline"
                                            href="/company/contact"
                                        />
                                    )
                                }}
                            />
                        </p>
                    )}
                    <div css={orSeparatorStyle}>
                        <span css={orSeparatorSpanStyle}>{separatorText}</span>
                    </div>
                    <SignInButtons />
                </form>
            )}
        </Form>
    );
};

const formErrorStyle = css({
    color: `${R400} !important`,
    margin: "6px 0 24px"
});

const orSeparatorStyle = css({
    borderBlockEnd: `.25px solid ${N100}`,
    lineHeight: ".25px",
    margin: "2rem 1rem",
    textAlign: "center"
});

const orSeparatorSpanStyle = css({
    backgroundColor: N0,
    padding: "0 1rem"
});
